/**
 * TimerControls.tsx
 * =============================================================================
 * Timer profile selector component.
 * Renders profile buttons (Ultradiano, Pomodoro, Libre, etc.)
 *
 * @component
 */

import React from 'react';
import type { AudioMode, TimerProfileId } from '../../core/types';
import { TIMER_PROFILES } from '../../core/types';

// =============================================================================
// TYPES
// =============================================================================

interface TimerControlsProps {
  currentModeId: AudioMode;
  currentTimerProfile: TimerProfileId;
  onSelectTimerProfile: (profileId: TimerProfileId) => void;
}

// =============================================================================
// TIMER PROFILE FILTERING BY MODE
// =============================================================================

const ALLOWED_PROFILES_BY_MODE: Record<AudioMode, TimerProfileId[]> = {
  focus: ['ultradian', 'pomodoro', 'free'], // Ultradiano (90min) es el default científico
  sleep: ['sleep_timer', 'free'], // Sleep timer (45min) es el default
  anxiety: ['reset_timer', 'free'], // Reset Express (15min) es el default científico
};

// =============================================================================
// COMPONENT
// =============================================================================

export const TimerControls: React.FC<TimerControlsProps> = ({
  currentModeId,
  currentTimerProfile,
  onSelectTimerProfile,
}) => {
  return (
    <div>
      <p className="text-[10px] text-neutral-500 uppercase tracking-wider mb-2 text-center">
        Perfil de Tiempo
      </p>
      <div className="grid grid-cols-3 md:grid-cols-2 gap-2 max-w-md mx-auto">
        {ALLOWED_PROFILES_BY_MODE[currentModeId]?.map((profileId) => {
          const profile = TIMER_PROFILES[profileId];
          const isSelected = currentTimerProfile === profileId;

          return (
            <button
              key={profileId}
              onClick={() => onSelectTimerProfile(profileId)}
              className={`
                px-2 py-1.5 rounded text-[11px] font-medium
                transition-all duration-200
                ${
                  isSelected
                    ? 'bg-white/15 text-neutral-100 border border-white/30'
                    : 'bg-white/5 text-neutral-400 border border-white/10 hover:bg-white/10 hover:text-neutral-200'
                }
              `}
              title={profile.name}
            >
              {profile.name}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TimerControls;
